package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PrepRoutineEditorScreen(
    onBack: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    val templateDay = "Template"
    var routines by remember { mutableStateOf<List<PrepRoutine>>(emptyList()) }
    var masterItems by remember { mutableStateOf<List<PrepItem>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }

    var showAddDialog by remember { mutableStateOf(false) }
    var routineToEdit by remember { mutableStateOf<PrepRoutine?>(null) }
    var routineToDelete by remember { mutableStateOf<PrepRoutine?>(null) }

    fun loadData() {
        scope.launch {
            isLoading = true
            repository.getPrepRoutines(templateDay).onSuccess { resRoutines ->
                routines = resRoutines
            }.onFailure {
                routines = emptyList()
            }
            repository.getMasterPrepItems().onSuccess { resMaster ->
                masterItems = resMaster
            }
            isLoading = false
        }
    }

    LaunchedEffect(Unit) {
        loadData()
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { ScreenTitle("Edit Templates") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                },
                actions = {
                    ToolbarPlusButton(onClick = { showAddDialog = true })
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier.padding(padding).fillMaxSize().padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (isLoading) {
                Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                    BrandLoadingSpinner()
                }
            } else if (routines.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text("No template items defined.", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.outline)
                        Button(onClick = { showAddDialog = true }) {
                            Text("Add Template Item")
                        }
                    }
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxWidth().weight(1f),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    items(routines, key = { it.id }) { routine ->
                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { routineToEdit = routine },
                            shape = MaterialTheme.shapes.medium,
                            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(16.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(
                                        text = routine.itemName ?: "Prep Item #${routine.prepItemId}",
                                        style = MaterialTheme.typography.titleMedium,
                                        fontWeight = FontWeight.Bold
                                    )
                                    Text("Default Qty: ${routine.prepQuantity ?: "1"}", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.secondary)
                                    if (!routine.notes.isNullOrBlank()) {
                                        Text("Notes: ${routine.notes}", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.outline)
                                    }
                                }

                                IconButton(onClick = { routineToDelete = routine }) {
                                    CloseIcon(tint = MaterialTheme.colorScheme.error)
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (showAddDialog) {
        RoutineDialog(
            title = "Add Template Item",
            masterItems = masterItems,
            onDismiss = { showAddDialog = false },
            onConfirm = { prepItemId, quantity, notes ->
                scope.launch {
                    repository.savePrepRoutine(
                        SavePrepRoutineRequest(
                            dayOfWeek = templateDay,
                            prepItemId = prepItemId,
                            prepQuantity = quantity,
                            notes = notes
                        )
                    ).onSuccess {
                        showAddDialog = false
                        loadData()
                        snackbarHostState.showSnackbar("Template item added successfully")
                    }
                }
            }
        )
    }

    routineToEdit?.let { routine ->
        RoutineDialog(
            title = "Edit Template Item",
            initialRoutine = routine,
            masterItems = masterItems,
            onDismiss = { routineToEdit = null },
            onConfirm = { prepItemId, quantity, notes ->
                scope.launch {
                    repository.savePrepRoutine(
                        SavePrepRoutineRequest(
                            id = routine.id,
                            dayOfWeek = templateDay,
                            prepItemId = prepItemId,
                            prepQuantity = quantity,
                            notes = notes
                        )
                    ).onSuccess {
                        routineToEdit = null
                        loadData()
                        snackbarHostState.showSnackbar("Template item updated successfully")
                    }
                }
            }
        )
    }

    routineToDelete?.let { routine ->
        AlertDialog(
            onDismissRequest = { routineToDelete = null },
            title = { Text("Delete Template Item") },
            text = { Text("Are you sure you want to delete this template item?") },
            confirmButton = {
                Button(
                    onClick = {
                        val routRem = routine
                        routineToDelete = null
                        scope.launch {
                            repository.deletePrepRoutine(routRem.id).onSuccess {
                                loadData()
                                snackbarHostState.showSnackbar("Template item deleted")
                            }
                        }
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                ) {
                    Text("Delete")
                }
            },
            dismissButton = {
                TextButton(onClick = { routineToDelete = null }) {
                    Text("Cancel")
                }
            },
            shape = MaterialTheme.shapes.large
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RoutineDialog(
    title: String,
    initialRoutine: PrepRoutine? = null,
    masterItems: List<PrepItem>,
    onDismiss: () -> Unit,
    onConfirm: (String, String, String) -> Unit
) {
    var selectedItem by remember {
        mutableStateOf(
            masterItems.find { it.id == initialRoutine?.prepItemId } ?: masterItems.firstOrNull()
        )
    }
    var quantity by remember { mutableStateOf(initialRoutine?.prepQuantity ?: "1") }
    var notes by remember { mutableStateOf(initialRoutine?.notes ?: "") }
    var expanded by remember { mutableStateOf(false) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(title) },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                if (masterItems.isEmpty()) {
                    Text("No master prep items available. Please create master prep items first.", color = MaterialTheme.colorScheme.error)
                } else {
                    Box(modifier = Modifier.fillMaxWidth()) {
                        OutlinedButton(
                            onClick = { expanded = true },
                            modifier = Modifier.fillMaxWidth(),
                            shape = MaterialTheme.shapes.small
                        ) {
                            Text(selectedItem?.itemName ?: "Select Master Prep Item")
                        }
                        DropdownMenu(
                            expanded = expanded,
                            onDismissRequest = { expanded = false },
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            masterItems.forEach { item ->
                                DropdownMenuItem(
                                    text = { Text(item.itemName) },
                                    onClick = {
                                        selectedItem = item
                                        expanded = false
                                    }
                                )
                            }
                        }
                    }
                }

                OutlinedTextField(
                    value = quantity,
                    onValueChange = { quantity = it },
                    label = { Text("Default Quantity") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = MaterialTheme.shapes.small
                )
                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    label = { Text("Notes (optional)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = MaterialTheme.shapes.small
                )
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    if (selectedItem != null && quantity.isNotBlank()) {
                        onConfirm(selectedItem!!.id, quantity, notes)
                    }
                },
                enabled = selectedItem != null && masterItems.isNotEmpty(),
                shape = MaterialTheme.shapes.medium
            ) {
                Text("Confirm")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        },
        shape = MaterialTheme.shapes.large
    )
}
